import { ChangeDetectionStrategy, Component } from '@angular/core';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { first } from 'rxjs/operators';

import { Ability } from '@recruitee/abilities-types';
import { CurrentUserSelectors, UserSelectors } from '@recruitee/account-data';
import { CurrentUser, User } from '@recruitee/account-types';
import { filterBoolean } from '@recruitee/common';
import { I18nService } from '@recruitee/i18n';
import { DialogComponentI, DialogRef } from '@recruitee/user-interface';

import { AdminsSelectDialogFormComponent } from '../../components/admins-select-dialog-form/admins-select-dialog-form.component';

export type AdminsSelectDialogData = Partial<{
  allowedUsers: User[];
  disallowedUsers: User[];
  assignments: number[];
  lockedAssignments: number[];
  lockedAssignmentsTooltip: string;
  heading: string;
  subheading: string;
  preventAdminRemove: boolean;
  showCountBeforeSubheading: boolean;
  minSelectedAdmins: number;
  maxSelectedAdmins: number;
  inviteModeEnabled?: boolean;
  shareOnTheGoEnabled?: boolean;
  disabledUserCopy?: string;
  actionLabel?: string;
  hasActionLabelCount?: boolean;
}>;

@Component({
  selector: 'rt-admins-select-dialog',
  templateUrl: './admins-select-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [AdminsSelectDialogFormComponent],
})
export class AdminsSelectDialogComponent
  implements DialogComponentI<AdminsSelectDialogData, User[] | null>
{
  public currentAdmin: CurrentUser;
  public assignments: number[] = [];
  public lockedAssignments: number[] = [];
  public lockedAssignmentsTooltip: string | null = null;
  public heading: string;
  public subheading: string;
  public preventAdminRemove: boolean = false;
  public allowedUsers: User[] = [];
  public disallowedUsers: User[] = [];
  public showCountBeforeSubheading: boolean = false;
  public minSelectedAdmins: number = 0;
  public maxSelectedAdmins: number = 0;
  public offerId?: number;
  public inviteModeEnabled = false;
  public disabledUserCopy: string;
  public disabledUsersHeader: string = this.i18n.translate(
    'app.candidate.admin_select.no_access.header',
  );
  public actionLabel?: string;
  public hasActionLabelCount: boolean;
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private dialog: DialogRef,
    private i18n: I18nService,
  ) {
    this.store
      .select(CurrentUserSelectors.data)
      .pipe(filterBoolean, first())
      .subscribe(currentAdmin => {
        this.currentAdmin = currentAdmin;
      });
  }

  public setDialogData(data: AdminsSelectDialogData) {
    this.assignments = data.assignments || [];
    this.lockedAssignments = data.lockedAssignments || [];
    this.lockedAssignmentsTooltip = data.lockedAssignmentsTooltip || null;
    this.heading = data.heading || this.i18n.translate('app.tasks.assignments.heading');
    this.subheading = data.subheading || this.i18n.translate('app.tasks.assignments.subheading');
    this.preventAdminRemove = data.preventAdminRemove || false;
    this.showCountBeforeSubheading = data.showCountBeforeSubheading || false;
    this.minSelectedAdmins = data.minSelectedAdmins || 0;
    this.maxSelectedAdmins = data.maxSelectedAdmins || 0;
    this.inviteModeEnabled = data.inviteModeEnabled ?? false;
    this.actionLabel = data.actionLabel;
    this.hasActionLabelCount = data.hasActionLabelCount ?? false;
    this.disabledUserCopy =
      data.disabledUserCopy ||
      this.i18n.translate('app.candidate.admin_select.no_access.tooltip_share_on_the_go');

    if (data.disallowedUsers) {
      this.disallowedUsers = data.disallowedUsers;
      if (
        this.disallowedUsers.every(user => !user.roleAbilities.includes(Ability.ViewCandidates))
      ) {
        this.disabledUsersHeader = this.i18n.translate(
          'app.candidate.admin_select.no_access.no_view_candidates.header',
        );
        this.disabledUserCopy = this.i18n.translate(
          'app.candidate.admin_select.no_access.no_view_candidates.tooltip',
        );
      }
    }

    if (data.allowedUsers) {
      this.allowedUsers = data.allowedUsers;
    } else {
      this.store
        .select(UserSelectors.entities())
        .pipe(first())
        .subscribe(allowedUsers => (this.allowedUsers = allowedUsers || []));
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public cancel(): void {
    this.dialog.close(null);
  }

  public submit(admins: User[]): void {
    this.dialog.close(admins);
  }

  public get currentAdminId(): number {
    return this.currentAdmin.id;
  }
}
